/** The accounts an API document version holds: Full (members'), Filleted (Companies House filing copy) or, on records made before APT341, Abridged. */
export type OutputProfile = 'MEMBERS' | 'ABRIDGED' | 'FILLETED';

export const DEFAULT_OUTPUT_PROFILE: OutputProfile = 'MEMBERS';

/**
 * APT341 (client, 28 Oct 16:50): "Remove the Abridged Accounts option for all accounting periods... From 1 April 2027, Companies House will no
 * longer accept Abridged Accounts submissions, including submissions relating to accounting periods ending before this date." What a user can
 * choose, generate, approve, finalise or file is Full or Filleted. An Abridged record made earlier still opens and is named, so the user can tell
 * what it is, but nothing can be done with it that would file it.
 */
export const ABRIDGED_RECORD_LABEL = 'Abridged accounts (no longer filed)';

/** The refusal the API gives for new Abridged accounts, said the same way when a screen has to stop the user first. */
export const ABRIDGED_RETIRED_MESSAGE = 'Abridged accounts can no longer be prepared or filed. Please use Filleted accounts.';

/** Every kind of accounts a stored record can be (names for display), including the retired Abridged. */
const KNOWN_OUTPUT_PROFILES: { code: OutputProfile; label: string }[] = [
  { code: 'MEMBERS', label: 'Full accounts' },
  { code: 'ABRIDGED', label: ABRIDGED_RECORD_LABEL },
  { code: 'FILLETED', label: 'Filleted accounts' },
];

/** The accounts a user can choose to prepare or file (selectors, filters): Full and Filleted. */
export const OUTPUT_PROFILES: { code: OutputProfile; label: string }[] = KNOWN_OUTPUT_PROFILES.filter((p) => p.code !== 'ABRIDGED');

/** An API that predates output profiles sends none: that is the Full accounts. */
export function outputProfileOf(row: { outputProfile?: string | null } | null | undefined): OutputProfile {
  const code = (row?.outputProfile ?? '').toUpperCase();
  return code === 'ABRIDGED' || code === 'FILLETED' ? code : DEFAULT_OUTPUT_PROFILE;
}

/** A selector's value: a link or stored value that names the retired Abridged accounts opens on the Full accounts instead. */
export function selectableOutputProfile(profile: OutputProfile): OutputProfile {
  return profile === 'ABRIDGED' ? DEFAULT_OUTPUT_PROFILE : profile;
}

export function outputProfileLabel(code: string | null | undefined): string {
  return KNOWN_OUTPUT_PROFILES.find((p) => p.code === (code ?? '').toUpperCase())?.label ?? KNOWN_OUTPUT_PROFILES[0].label;
}

/** A report's variant (0 Full, 1 Abridged, 2 Filleted) as the output profile of the same accounts. */
export function outputProfileOfVariant(accountsVariant: number | null | undefined): OutputProfile {
  return accountsVariant === 1 ? 'ABRIDGED' : accountsVariant === 2 ? 'FILLETED' : DEFAULT_OUTPUT_PROFILE;
}

/** The output profile a report list row's Type text names ("Abridged Accounts", "Filleted Accounts", else Full). */
export function outputProfileOfTypeName(type: string | null | undefined): OutputProfile {
  const text = (type ?? '').toLowerCase();
  return text.includes('abridged') ? 'ABRIDGED' : text.includes('filleted') ? 'FILLETED' : DEFAULT_OUTPUT_PROFILE;
}
